import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier: string | null;
  subscription_end: string | null;
  is_beta_tester: boolean;
  trial_end: string | null;
  trial_days: number;
  is_admin: boolean;
}

const CACHE_KEY = "subscription_cache";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedSubscription {
  data: SubscriptionData;
  timestamp: number;
  userId: string;
}

const getCache = (userId: string): SubscriptionData | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedSubscription = JSON.parse(raw);
    if (cached.userId !== userId) return null;
    if (Date.now() - cached.timestamp > CACHE_TTL) return null;
    return cached.data;
  } catch {
    return null;
  }
};

const setCache = (userId: string, data: SubscriptionData) => {
  try {
    const cached: CachedSubscription = { data, timestamp: Date.now(), userId };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {}
};

const clearCache = () => {
  try { localStorage.removeItem(CACHE_KEY); } catch {}
};

const DEFAULT_SUBSCRIPTION: SubscriptionData = {
  subscribed: false,
  subscription_tier: null,
  subscription_end: null,
  is_beta_tester: false,
  trial_end: null,
  trial_days: 30,
  is_admin: false,
};

export const useSubscription = () => {
  const { user, session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionData>(() => {
    // Initialize from cache if available
    if (user?.id) {
      const cached = getCache(user.id);
      if (cached) return cached;
    }
    return DEFAULT_SUBSCRIPTION;
  });
  const hasInitializedFromCache = useRef(false);

  // When user becomes available, check cache immediately
  useEffect(() => {
    if (user?.id && !hasInitializedFromCache.current) {
      const cached = getCache(user.id);
      if (cached) {
        setSubscription(cached);
        setLoading(false);
        hasInitializedFromCache.current = true;
      }
    }
    if (!user) {
      hasInitializedFromCache.current = false;
    }
  }, [user?.id]);

  const checkSubscription = useCallback(async () => {
    if (!session) {
      setLoading(false);
      return;
    }

    try {
      // Don't show loading if we have cache
      const hasCachedData = user?.id ? !!getCache(user.id) : false;
      if (!hasCachedData) {
        setLoading(true);
      }

      // Run all 3 queries in parallel
      const [subscriptionResult, subscriberResult, userRoleResult] = await Promise.all([
        supabase.functions.invoke('check-subscription', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        user?.email
          ? supabase.from('subscribers').select('is_beta_tester, trial_end, trial_days').eq('email', user.email).single()
          : Promise.resolve({ data: null, error: null }),
        user?.id
          ? supabase.from('user_roles').select('role').eq('user_id', user.id).eq('role', 'admin').single()
          : Promise.resolve({ data: null, error: null }),
      ]);

      const { data, error } = subscriptionResult;

      // Handle authentication errors
      if (error || data?.auth_error) {
        let isAuthError = data?.auth_error || false;
        let errorDetails: any = null;
        
        if (error && 'context' in error) {
          try {
            const funcError = error as any;
            if (funcError.context?.body) {
              errorDetails = JSON.parse(funcError.context.body);
              isAuthError = errorDetails?.auth_error || false;
            }
          } catch {}
        }
        
        const errorMessage = error?.message || '';
        isAuthError = isAuthError || 
          errorMessage.includes('session') || 
          errorMessage.includes('auth') ||
          errorMessage.includes('401') ||
          errorMessage.includes('JWT') ||
          errorMessage.includes('non-2xx');
        
        console.error('Error checking subscription:', error || data?.error, { isAuthError, errorDetails });
        
        if (isAuthError) {
          console.log('Session invalid, signing out user');
          setSubscription(DEFAULT_SUBSCRIPTION);
          clearCache();
          setLoading(false);
          await supabase.auth.signOut();
          return;
        }
        
        toast({
          title: "Erreur",
          description: "Impossible de vérifier le statut d'abonnement",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const subscriberData = subscriberResult.data;
      const isAdmin = !!userRoleResult.data;

      const newSubscription: SubscriptionData = {
        subscribed: data.subscribed || false,
        subscription_tier: data.subscription_tier || null,
        subscription_end: data.subscription_end || null,
        is_beta_tester: (subscriberData as any)?.is_beta_tester || false,
        trial_end: (subscriberData as any)?.trial_end || null,
        trial_days: (subscriberData as any)?.trial_days || 30,
        is_admin: isAdmin,
      };

      setSubscription(newSubscription);
      if (user?.id) setCache(user.id, newSubscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [session, user?.email, user?.id]);

  const createCheckout = async (planType: 'monthly' | 'annual', promoCode?: string) => {
    if (!session) {
      toast({ title: "Erreur", description: "Vous devez être connecté pour souscrire", variant: "destructive" });
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { planType, promoCode },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) {
        toast({ title: "Erreur", description: "Impossible de créer la session de paiement", variant: "destructive" });
        return;
      }
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({ title: "Erreur", description: "Une erreur est survenue", variant: "destructive" });
    }
  };

  const openCustomerPortal = async () => {
    if (!session) {
      toast({ title: "Erreur", description: "Vous devez être connecté", variant: "destructive" });
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) {
        toast({ title: "Erreur", description: "Impossible d'ouvrir le portail client", variant: "destructive" });
        return;
      }
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({ title: "Erreur", description: "Une erreur est survenue", variant: "destructive" });
    }
  };

  const isTrialActive = () => {
    if (!subscription.trial_end) return false;
    return new Date(subscription.trial_end) > new Date();
  };

  const getTrialDaysRemaining = () => {
    if (!subscription.trial_end) return 0;
    const trialEnd = new Date(subscription.trial_end);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const isSubscriptionPeriodActive = () => {
    if (!subscription.subscription_end) return false;
    return new Date(subscription.subscription_end) > new Date();
  };

  const hasPremiumAccess = () => {
    const hasActiveSubscription = subscription.subscribed || isSubscriptionPeriodActive();
    return hasActiveSubscription || subscription.is_beta_tester || subscription.is_admin || isTrialActive();
  };

  useEffect(() => {
    if (user) {
      checkSubscription();
    } else {
      clearCache();
    }
  }, [user, checkSubscription]);

  return {
    subscription,
    loading,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    isTrialActive: isTrialActive(),
    trialDaysRemaining: getTrialDaysRemaining(),
    hasPremiumAccess: hasPremiumAccess(),
  };
};
